import type { SiteSnapshot } from "@/data/types";
import { signedKW, unsignedKW } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { scada } from "../scadaColors";

/**
 * Site loads — behind-the-meter node. Factory + warehouse silhouette.
 *
 * Composition: a sawtooth-roof workshop on the left, a flat-roof warehouse
 * on the right with stacked docks, and a stack with optional steam plume
 * when load is heavy. Designed to read at smaller sizes too.
 */
export function FactoryNode({
  cx, cy, snap, detail = true,
}: {
  cx: number; cy: number; snap: SiteSnapshot;
  /** Summary mode (false) hides the self-sufficiency strip. */
  detail?: boolean;
}) {
  const t = useT();
  const GREEN = scada.gridExport();   // export green (matches the light palette)
  const w = snap.building_load_W;
  const heavy = w > 300_000;
  const selfSuff = Math.min(100, snap.energy_today.self_sufficiency_pct);
  const gid = `fac-grad-${Math.round(cx)}-${Math.round(cy)}`;
  const bgid = `fac-bg-${Math.round(cx)}-${Math.round(cy)}`;

  return (
    <g style={{ cursor: "pointer" }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="var(--scada-surface-mid)" />
          <stop offset="100%" stopColor="var(--scada-surface-lo)" />
        </linearGradient>
        <linearGradient id={bgid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="var(--scada-surface-hi)" />
          <stop offset="100%" stopColor="var(--scada-surface-mid)" />
        </linearGradient>
      </defs>

      {/* Drop shadow */}
      <ellipse cx={cx} cy={cy + 60} rx={70} ry={5} fill="var(--scada-shadow)" />

      {/* Background plate */}
      <rect x={cx - 78} y={cy - 60} width={156} height={120} rx={14}
        fill={`url(#${bgid})`} stroke={GREEN} strokeOpacity={0.45} strokeWidth={1.4} />
      <rect x={cx - 78} y={cy - 60} width={156} height={16} rx={14}
        fill={GREEN} fillOpacity={0.10} />
      <text x={cx - 70} y={cy - 49} fontSize={8} fontWeight={700} fill={GREEN} letterSpacing={1.2}>
        {`${t("monitor.scada.zone.behindMeter").toUpperCase()} · ${t("monitor.scada.node.loads")}`}
      </text>
      <circle cx={cx + 66} cy={cy - 52} r={2.2} fill={GREEN} />

      {/* Smokestack with optional steam plume */}
      <g transform={`translate(${cx - 60} ${cy - 36})`}>
        <rect x={0} y={20} width={7} height={36} rx={0.5}
          fill="var(--surface-2)" stroke={GREEN} strokeWidth={1.1} />
        <rect x={-1} y={18} width={9} height={4} rx={0.4} fill={GREEN} />
        {heavy && (
          <g>
            {[0, 1, 2].map((i) => (
              <circle key={i} cx={3 + i} cy={14 - i * 2} r={3 + i * 0.5}
                fill="var(--text-muted)" opacity={0.45 - i * 0.1}>
                <animate attributeName="cy" values={`${14 - i * 2};${4 - i * 2};${14 - i * 2}`}
                  dur={`${3 + i * 0.4}s`} repeatCount="indefinite" />
                <animate attributeName="opacity" values={`${0.45 - i * 0.1};0;${0.45 - i * 0.1}`}
                  dur={`${3 + i * 0.4}s`} repeatCount="indefinite" />
              </circle>
            ))}
          </g>
        )}
      </g>

      {/* Workshop with sawtooth roof (left half) */}
      <g>
        <path
          d={`
            M${cx - 46},${cy - 18}
            L${cx - 46},${cy - 6}
            L${cx - 36},${cy - 18}
            L${cx - 26},${cy - 6}
            L${cx - 16},${cy - 18}
            L${cx - 6},${cy - 6}
            L${cx - 6},${cy + 26}
            L${cx - 46},${cy + 26}
            Z
          `}
          fill={`url(#${gid})`} stroke={GREEN} strokeWidth={1.3}
        />
        {/* Sawtooth window panes */}
        {[0, 1, 2].map((i) => (
          <line key={i}
            x1={cx - 46 + i * 20 + 1} y1={cy - 17}
            x2={cx - 46 + i * 20 + 9} y2={cy - 7}
            stroke={GREEN} strokeOpacity={0.55} strokeWidth={0.9} />
        ))}
        {/* Wall windows */}
        {[0, 1, 2, 3].map((i) => (
          <rect key={i} x={cx - 42 + i * 9} y={cy - 2} width={6} height={5}
            fill={GREEN} fillOpacity={0.20} stroke={GREEN} strokeOpacity={0.5} strokeWidth={0.5} />
        ))}
        {/* Side door */}
        <rect x={cx - 12} y={cy + 14} width={5} height={12} fill="var(--surface-3)" stroke={GREEN} strokeWidth={0.7} />
      </g>

      {/* Warehouse (right half) — flat roof with cylindrical vents */}
      <g>
        <rect x={cx - 4} y={cy - 14} width={50} height={40} rx={1}
          fill={`url(#${gid})`} stroke={GREEN} strokeWidth={1.3} />
        {/* Roof line */}
        <line x1={cx - 4} y1={cy - 8} x2={cx + 46} y2={cy - 8} stroke={GREEN} strokeOpacity={0.45} strokeWidth={0.6} />
        {/* Roof vents */}
        {[0, 1, 2].map((i) => (
          <ellipse key={i} cx={cx + 4 + i * 16} cy={cy - 16} rx={3} ry={1.2}
            fill={GREEN} opacity={0.6} />
        ))}
        {/* Loading dock with rolling door (large) */}
        <rect x={cx + 4} y={cy + 4} width={20} height={22} rx={1}
          fill="var(--surface-3)" stroke={GREEN} strokeWidth={0.9} />
        {[0, 1, 2, 3, 4].map((i) => (
          <line key={i} x1={cx + 4} y1={cy + 8 + i * 4}
            x2={cx + 24} y2={cy + 8 + i * 4}
            stroke={GREEN} strokeOpacity={0.3} strokeWidth={0.4} />
        ))}
        {/* Solar panels on warehouse roof — only if topology has PV (callout above) */}
        {/* Window strip */}
        {[0, 1, 2].map((i) => (
          <rect key={i} x={cx + 28 + i * 6} y={cy - 2} width={4} height={5}
            fill={GREEN} fillOpacity={0.2} stroke={GREEN} strokeOpacity={0.4} strokeWidth={0.5} />
        ))}
      </g>

      {/* Ground line / parking */}
      <line x1={cx - 60} y1={cy + 26} x2={cx + 60} y2={cy + 26}
        stroke={GREEN} strokeWidth={1.4} />
      {/* Parking lot ticks */}
      {[0, 1, 2, 3].map((i) => (
        <line key={i} x1={cx - 56 + i * 32} y1={cy + 28}
          x2={cx - 50 + i * 32} y2={cy + 28}
          stroke={GREEN} strokeOpacity={0.5} strokeWidth={0.7} />
      ))}

      {/* Self-sufficiency strip — secondary, collapsed in Summary mode. */}
      {detail && (
        <g transform={`translate(${cx - 70} ${cy + 36})`}>
          {/* Native SVG tooltip expanding the abbreviation (OVW-12). */}
          <title>{t("monitor.scada.node.selfSuffTip")}</title>
          <rect width={140} height={20} rx={4} fill="var(--surface-3)" stroke="var(--edge)" strokeWidth={0.7} />
          <text x={6} y={12} fontSize={7.5} fill="var(--text-faint)" letterSpacing={0.3}>{t("monitor.scada.node.selfSuff")}</text>
          <rect x={62} y={5} width={64} height={10} rx={2} fill="var(--surface-2)" stroke="var(--edge)" strokeWidth={0.5} />
          <rect x={62} y={5} width={64 * (selfSuff / 100)} height={10} rx={2} fill={GREEN} opacity={0.85} />
          <text x={130} y={12} textAnchor="end" fontSize={9} fontWeight={700} fill="var(--text)" className="num">
            {selfSuff.toFixed(0)}%
          </text>
        </g>
      )}

      {/* Footer — show signed net building load so a PV-surplus state
          (negative) is visible. The discharge tone flags export. */}
      <text x={cx} y={cy + 76} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--text)">
        {t("monitor.now.netBuildingLoad")}
      </text>
      <text x={cx} y={cy + 90} textAnchor="middle" fontSize={14} fontWeight={800}
        className="num" fill={w < -500 ? scada.discharge() : GREEN}>
        {signedKW(w)} kW
      </text>
      <text x={cx} y={cy + 102} textAnchor="middle" fontSize={9} fill="var(--text-muted)">
        {w < -500 ? t("monitor.scada.node.surplusToGrid") : t("monitor.scada.node.industrial")} · {t("monitor.now.kwhToday", { kwh: (snap.energy_today.bess_discharge_kWh + snap.energy_today.pv_kWh + snap.energy_today.grid_import_kWh).toFixed(0) })}
      </text>
    </g>
  );
}
